import {
  DocumentMap,
  EducationSponsorshipMap,
  FamilySponsorshipMap,
  FamilySponsorType,
  IDocument,
  IDocuments,
  IDocumentsList,
  IFinance,
  IFinanceDocuments,
  IFollowUpDocuments,
} from '../types';
import { hashToArray } from './helpers';
import { Status } from '../components/atoms/finger-doc';
import { accountTypeLabel } from '../components/organisms/finance/BankSponsor/utils';
import { DocAccessType, EducationSponsorship, IFollowUpArea } from 'api-paths-cas';
import { UserRole } from '@/store/auth/types';
import { isAgentOrAgencyRole, isEnrolyStaff, isPartnerRole, isUniStaffRole } from './roles/permissions';

type SortReturn = -1 | 0 | 1;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function sortFn(a: { key: string; item: any }, b: { key: string; item: any }): SortReturn {
  // Sort documents by 'uploadedAt'
  try {
    // TODO - confirm removal of case 1 and 2 as this is incorrect for
    // where there are document rejections

    // CASE 1: Check if 'uploadedAt' property exists in a and not in b
    // if ('uploadedAt' in a.item && !('uploadedAt' in b.item)) {
    //   console.log('case1', a.key, b.key);
    //   return 1; // 'a' should come after 'b'
    // }
    // CASE 2: Check if 'uploadedAt' property exists in b and not in a
    // if (!('uploadedAt' in a.item) && 'uploadedAt' in b.item) {
    //   console.log('case2', a.key, b.key);
    //   return -1; // 'b' should come after 'a'
    // }
    // If both have 'uploadedAt' or neither have it, continue with sorting
    if ('uploadedAt' in a.item && 'uploadedAt' in b.item) {
      const uploadedAtA = a.item.uploadedAt;
      const uploadedAtB = b.item.uploadedAt;
      if (uploadedAtA > uploadedAtB) {
        return 1;
      } else if (uploadedAtA < uploadedAtB) {
        return -1;
      }
    }
    // Sort documents by extracted numbers from the 'key' property of the items
    const firstRecNum = extractNumber(a.key);
    const secondRecNum = extractNumber(b.key);

    if (firstRecNum !== null && secondRecNum !== null) {
      return String(firstRecNum).localeCompare(String(secondRecNum)) as SortReturn;
    } else if (firstRecNum === null && secondRecNum === null) {
      return a.key.localeCompare(b.key) as SortReturn;
    }
    return firstRecNum !== null ? 1 : -1;
  } catch (e) {
    return 0;
  }
}

function extractNumber(key: string): number | null {
  const numStr = key.substring(key.lastIndexOf('-') + 1);
  const num = Number(numStr);
  return isNaN(num) ? null : num;
}

export function getVisible<K extends { [key: string]: unknown } = DocumentMap, T = IDocumentsList>({
  documentsHash,
  userAccess,
  infoOnly,
  userRole,
}: {
  documentsHash: K;
  userAccess?: DocAccessType;
  infoOnly?: boolean;
  userRole?: UserRole;
}): T[] {
  const docs = hashToArray(documentsHash);

  return docs
    .filter((doc: IDocumentsList) => {
      if (doc.item.status === 'DELETED') {
        return false;
      }

      const visibleForStaff = userAccess === DocAccessType.STAFF_ONLY;
      const isStaffOnlyDoc = doc.item?.userAccess === DocAccessType.STAFF_ONLY;

      if (infoOnly) {
        return hasDocumentType(doc.item, 'infoOnly');
      }

      if (!userAccess) {
        return hasDocumentType(doc.item, 'required');
      }

      const docType = visibleForStaff && isStaffOnlyDoc ? 'staffOnly' : 'agentOnly';
      return hasDocumentType(doc.item, docType, userRole, userAccess);
    })
    .sort(sortFn) as T[];
}

export const hasDeleted = (documentsHash: DocumentMap): boolean => {
  return Object.values(documentsHash ?? {}).some(doc => doc.status === 'DELETED');
};

export const hasDocumentType = (
  doc: IDocument,
  type: string | null,
  userRole?: UserRole,
  userAccess?: DocAccessType,
) => {
  let hasDocType: boolean;
  switch (type) {
    case 'required':
      hasDocType =
        [
          'AWAITING',
          'UPLOADED_OK',
          'UPLOADED_WITH_ERROR',
          'REJECTED',
          'REJECTED_WITHOUT_ALTERNATIVE',
          'APPROVED',
          'REPLACED',
          'DELETED',
          'SKIPPED',
        ].includes(doc.status) && !doc.userAccess;
      break;
    case 'infoOnly':
      hasDocType = ['INFO_ONLY', 'NOT_ACKNOWLEDGED', 'ACKNOWLEDGED'].includes(doc.status);
      break;
    case 'staffOnly':
      hasDocType = doc.userAccess === DocAccessType.STAFF_ONLY;
      break;
    case 'agentOnly':
      hasDocType = doc.userAccess === DocAccessType.AGENT_ONLY;
      break;
    default:
      hasDocType = false;
  }

  if (userRole) {
    const isVisible = isDocVisibleToRole(userRole, doc, userAccess);
    hasDocType = hasDocType && isVisible;
  }

  return hasDocType;
};

export const hasAnyMatchingDocs = (
  docs: IDocuments,
  type: string,
  predicate: (doc: IDocument, type: string) => boolean = hasDocumentType,
): boolean => {
  let hasFoundMatch = false;

  Object.keys(docs).some((key: string, _index: number) => {
    if (hasFoundMatch) {
      return true; // break out of loop
    }
    switch (key) {
      case 'cas':
      case 'academicTranscript':
      case 'academicCertificate':
      case 'parentalConsent':
      case 'tbTest':
      case 'atas':
      case 'englishTest':
      case 'englishQualification':
      case 'postStudyLetter': {
        Object.values(docs[key]).some((doc: IDocument) => {
          if (!hasFoundMatch && predicate(doc, type)) {
            hasFoundMatch = true;
            return true; // breaks out of loop
          }
          return false;
        });
        return hasFoundMatch;
      }
      case 'birthCertificate': {
        if (docs[key].certificate) {
          Object.values(docs[key].certificate!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].translation) {
          Object.values(docs[key].translation!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      case 'passport': {
        if (docs[key].current) {
          Object.values(docs[key].current!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].renew) {
          Object.values(docs[key].renew!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      case 'studentVisas': {
        Object.keys(docs[key]).some((studentVisasKey: string) => {
          const previousStudentVisa = docs[key][studentVisasKey];
          if (previousStudentVisa.visa) {
            Object.values(previousStudentVisa.visa).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (previousStudentVisa.cas) {
            Object.values(previousStudentVisa.cas).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (previousStudentVisa.biometric) {
            Object.values(previousStudentVisa.biometric).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          return false;
        });
        return hasFoundMatch;
      }
      case 'visaRefusals':
      case 'nonUkVisaRefusals':
      case 'otherVisas': {
        Object.keys(docs[key]).some((visaKey: string) => {
          const visaDoc = docs[key][visaKey];

          Object.values(visaDoc).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });

          return false;
        });
        return hasFoundMatch;
      }
      case 'customRequest': {
        Object.keys(docs[key]).some((requestKey: string) => {
          const requestDoc = docs[key][requestKey];
          return Object.values(requestDoc).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        });
        return hasFoundMatch;
      }
      default: {
        return false;
      }
    }
  });

  return hasFoundMatch;
};

export const hasAnyMatchingFinDocs = (
  docs: IFinanceDocuments,
  type: string,
  predicate: (doc: IDocument, type: string) => boolean = hasDocumentType,
): boolean => {
  let hasFoundMatch = false;
  Object.keys(docs).some((key: string, _index: number) => {
    if (hasFoundMatch) {
      return true; // break out of loop
    }
    switch (key) {
      case 'educationSponsorship':
      case 'customRequest':
      case 'scholarshipSponsorship':
      case 'governmentSponsorship':
      case 'companySponsorship': {
        Object.keys(docs[key]).some((docKey: string) => {
          const sponsorshipDoc = docs[key][docKey];
          return Object.values(sponsorshipDoc).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        });
        return hasFoundMatch;
      }
      case 'deposit': {
        if (docs[key]) {
          return Object.values(docs[key]).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      case 'birthCertificate': {
        if (docs[key].certificate) {
          Object.values(docs[key].certificate!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].translation) {
          Object.values(docs[key].translation!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      case 'familySponsorship': {
        Object.keys(docs[key]).some((docKey: string) => {
          const sponsorshipDoc = docs[key][docKey];
          if (sponsorshipDoc.bankStatement) {
            Object.values(sponsorshipDoc.bankStatement).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }

          if (sponsorshipDoc.bankStatementTranslation) {
            Object.values(sponsorshipDoc.bankStatementTranslation!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (sponsorshipDoc.bankLetter) {
            Object.values(sponsorshipDoc.bankLetter!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (sponsorshipDoc.parentLetter) {
            Object.values(sponsorshipDoc.parentLetter!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (sponsorshipDoc.verificationBankLetter) {
            Object.values(sponsorshipDoc.verificationBankLetter!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (sponsorshipDoc.secondaryBankStatement) {
            Object.values(sponsorshipDoc.secondaryBankStatement!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          return false;
        });
        return hasFoundMatch;
      }
      case 'evidenceOfIncome': {
        if (docs[key].curriculumVitae) {
          Object.values(docs[key].curriculumVitae!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].employmentReference) {
          Object.values(docs[key].employmentReference!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].recentEmploymentContract) {
          Object.values(docs[key].recentEmploymentContract!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].recentPayslip) {
          Object.values(docs[key].recentPayslip ?? {}).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      default: {
        return false;
      }
    }
  });

  return hasFoundMatch;
};

export const hasAnyMatchingFollowUpDocs = (
  docs: IFollowUpDocuments,
  areaType: 'beforeTravel' | 'afterArrival',
  type: string,
  predicate: (doc: IDocument, type: string) => boolean = hasDocumentType,
): boolean => {
  let hasFoundMatch = false;
  switch (areaType) {
    case 'beforeTravel': {
      Object.keys(docs[areaType]).some((docType: string) => {
        if (hasFoundMatch) {
          return true; // break out of loop
        }
        switch (docType) {
          case 'studentRefusalLetter':
          case 'visaVignette':
          case 'travelTicket':
          case 'immigrationStatusShareCode':
          case 'immigrationStatusPdfCopy':
          case 'visaApplicationPdfCopy': {
            Object.values(docs[areaType][docType] ?? {}).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
            return hasFoundMatch;
          }
          case 'customRequest': {
            if (docs[areaType].customRequest) {
              Object.keys(docs[areaType].customRequest ?? {}).some((requestKey: string) => {
                const requestDoc = docs[areaType].customRequest[requestKey];
                return Object.values(requestDoc ?? {}).some((doc: IDocument) => {
                  if (!hasFoundMatch && predicate(doc, type)) {
                    hasFoundMatch = true;
                    return true; // breaks out of loop
                  }
                  return false;
                });
              });
              return hasFoundMatch;
            }
            return hasFoundMatch;
          }
        }
        return hasFoundMatch;
      });
      break;
    }
    case 'afterArrival': {
      Object.keys(docs[areaType]).some((docType: string) => {
        if (hasFoundMatch) {
          return true; // break out of loop
        }

        switch (docType) {
          case 'stampedVisaVignette':
          case 'biometricResidencePermit': {
            Object.values(docs[areaType][docType] ?? {}).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
            return hasFoundMatch;
          }
          case 'customRequest': {
            if (docs[areaType].customRequest ?? {}) {
              Object.keys(docs[areaType].customRequest ?? {}).some((requestKey: string) => {
                const requestDoc = docs[areaType].customRequest[requestKey];
                return Object.values(requestDoc ?? {}).some((doc: IDocument) => {
                  if (!hasFoundMatch && predicate(doc, type)) {
                    hasFoundMatch = true;
                    return true; // breaks out of loop
                  }
                  return false;
                });
              });
              return hasFoundMatch;
            }
            return hasFoundMatch;
          }
        }
        return hasFoundMatch;
      });
      break;
    }
    default: {
      hasFoundMatch = false;
    }
  }

  return hasFoundMatch;
};

/**
 * Determines if a document is visible to a user based on their role and access type.
 *
 * @param userRole - The role of the user attempting to access the document.
 * @param doc - The document to check visibility for.
 * @param userAccess - Optional access type indicating the section in which the document is being rendered.
 *
 * @returns A boolean indicating whether the document is visible to the user.
 *
 * The function checks the user's role against the document's access type and determines visibility based on:
 * - Admin, Enroly staff, or Partner roles can view all documents.
 * - Agent-only documents are visible to agents, Uni staff, or Enroly staff only in the agent section.
 * - Staff-only documents are visible to Uni staff or Enroly staff only in the staff section.
 * - If none of the conditions are met, the document is considered visible by default.
 */
export function isDocVisibleToRole(userRole: UserRole, doc: IDocument, userAccess?: DocAccessType) {
  const isEnrolyStaffUser = isEnrolyStaff(userRole);
  const isPartner = isPartnerRole(userRole);
  const isAgent = isAgentOrAgencyRole(userRole);
  const isUniStaff = isUniStaffRole(userRole);

  // Admin, enroly or partner can see all documents
  const isEnrolyOrPartner = isEnrolyStaffUser || isPartner;
  const docAccessType = doc.userAccess;
  const viewingInSection = userAccess;

  // Regular documents (no access restrictions) are always visible
  if (!docAccessType) {
    return true;
  }

  // Must be viewing in the correct section to see restricted documents
  const isViewingInCorrectSection = docAccessType === viewingInSection;
  if (!isViewingInCorrectSection) {
    return false;
  }

  // AGENT_ONLY documents can be seen by: agents, uni staff, enroly staff, or partners
  if (docAccessType === DocAccessType.AGENT_ONLY) {
    return isAgent || isUniStaff || isEnrolyOrPartner;
  }

  // STAFF_ONLY documents can be seen by: uni staff, enroly staff, or partners
  if (docAccessType === DocAccessType.STAFF_ONLY) {
    return isUniStaff || isEnrolyOrPartner;
  }

  // Unknown document access type - deny by default
  return false;
}

export function bankStatementName(
  sponsorType: (typeof FamilySponsorType)[keyof typeof FamilySponsorType],
): 'Term Deposit Statement Scan' | 'Bank Statement Scan' {
  switch (sponsorType) {
    case FamilySponsorType.TERM_DEPOSIT:
      return 'Term Deposit Statement Scan';
    case FamilySponsorType.STANDARD:
    case FamilySponsorType.SAVINGS:
    case FamilySponsorType.INVESTMENT:
    case FamilySponsorType.PENSION:
    default:
      return 'Bank Statement Scan';
  }
}

export function bankStatementTransName(
  sponsorType: (typeof FamilySponsorType)[keyof typeof FamilySponsorType],
): 'Term Deposit Statement Translation' | 'Bank Statement Translation' {
  switch (sponsorType) {
    case FamilySponsorType.TERM_DEPOSIT:
      return 'Term Deposit Statement Translation';
    case FamilySponsorType.STANDARD:
    case FamilySponsorType.SAVINGS:
    case FamilySponsorType.INVESTMENT:
    case FamilySponsorType.PENSION:
    default:
      return 'Bank Statement Translation';
  }
}

export function bankLetterName(
  sponsorType: (typeof FamilySponsorType)[keyof typeof FamilySponsorType],
): 'Term Deposit Letter' | 'Bank Letter' {
  switch (sponsorType) {
    case FamilySponsorType.TERM_DEPOSIT:
      return 'Term Deposit Letter';
    case FamilySponsorType.STANDARD:
    case FamilySponsorType.SAVINGS:
    case FamilySponsorType.INVESTMENT:
    case FamilySponsorType.PENSION:
    default:
      return 'Bank Letter';
  }
}

export function getLeadSponsorByKey(finance: IFinance): string {
  const familyType: string | undefined = Object.values(finance.familySponsorship)?.[0]?.accountType;
  const eduType: string | undefined = Object.values(finance.educationSponsorship)?.[0]?.personName;
  let type: string = (familyType ? accountTypeLabel(familyType) : '') || eduType;
  type = ['Mine', 'Joint', 'Other'].includes(type) ? 'Student' : type;
  return type;
}

export function getFingerStatus(doc: IDocument): Status {
  return doc.status === 'APPROVED'
    ? Status.Approved
    : doc.status === 'REJECTED'
      ? Status.Rejected
      : doc.status === 'REJECTED_WITHOUT_ALTERNATIVE'
        ? Status.RejectedWithoutAlt
        : doc.status === 'UPLOADED_OK'
          ? Status.UploadedOK
          : doc.status === 'UPLOADED_WITH_ERROR'
            ? Status.UploadedError
            : doc.status === 'SKIPPED'
              ? Status.Skipped
              : doc.status === 'REPLACED'
                ? Status.Replaced
                : doc.status === 'ACKNOWLEDGED'
                  ? Status.Acknowledged
                  : doc.status === 'NOT_ACKNOWLEDGED'
                    ? Status.NotAcknowledged
                    : doc.status === 'INFO_ONLY'
                      ? Status.InfoOnly
                      : Status.Awaiting;
}

// Choose which sponsor owns the Financial Birth Certificate.
export function findBirthCertSponsor(
  familySponsorship: FamilySponsorshipMap,
  educationSponsorship: EducationSponsorshipMap,
): string | null {
  const findFam = hashToArray(familySponsorship).find(({ item }) => {
    return ['Mother', 'Father', 'Guardian'].includes(item.accountType);
  });
  if (findFam) {
    return findFam.key;
  }

  const findEdu:
    | {
        key: string;
        item: EducationSponsorship;
      }
    | undefined = hashToArray(educationSponsorship).find(({ item }) => {
    return ['Mother', 'Father', 'Guardian'].includes(item.personName);
  });
  if (findEdu) {
    return findEdu.key;
  }

  return null;
}

export function getDocDetails(data: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  docs: any;
  sponsorType: string;
  sponsorKey: string | null;
  docType: string | null;
  docKey: string | null;
}): IDocument {
  const { docs, sponsorType, sponsorKey, docType, docKey } = data;

  let docDetails;

  if (sponsorType && sponsorKey && docType && docKey) {
    if ([IFollowUpArea.BEFORE_TRAVEL, IFollowUpArea.AFTER_ARRIVAL].includes(sponsorType as IFollowUpArea)) {
      // follow-up custom docs
      docDetails = docs[sponsorType][docType][sponsorKey][docKey];
    } else {
      docDetails = docs[sponsorType][sponsorKey][docType][docKey];
    }
  } else if (sponsorType && docType === null && sponsorKey === null && docKey) {
    docDetails = docs[sponsorType][docKey];
  } else if (sponsorType && docType === null && sponsorKey && docKey) {
    docDetails = docs[sponsorType][sponsorKey][docKey];
  } else if (sponsorType === null && sponsorKey === null && docType && docKey) {
    docDetails = docs[docType][docKey];
  } else if (sponsorType && sponsorKey === null && docType && docKey) {
    docDetails = docs[sponsorType][docType][docKey];
  }

  return docDetails;
}
